
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { GetStaticPropsContext } from 'next/types';
import { Page as MakeswiftPage, PageProps as MakeswiftPageProps } from '@makeswift/runtime/next';
import { ArticleFeedCarouselDocument } from '@/generated/contentful';
import { client as contentful } from '@/lib/contentful/client';
import { getBaseUrl, serializeCacheKey } from '@/lib/contentful/utils';
import { getPageSnapshot } from '@/lib/makeswift/client';
import '@/lib/makeswift/components';
import { runtime } from '@/lib/makeswift/runtime';
import { getSeoLocale } from '@/utils/locales/getSeoLocale';
import { getLocalizedFromEnglish } from '@/utils/locales/localized-routes/getLocalizedFromEnglish';
import { getNoIndexValueFromMakeSwift } from '@/utils/seo/getNoIndexValueFromMakeSwift';
import { useUrl } from '@/utils/url/useUrl';
import { getArticleParams } from '@/components/Contentful/entries/Article/ArticleFeedCarousel';
import { PreviewToast } from '@/components/PreviewToast';
type Props = MakeswiftPageProps & {
    preview: boolean;
};
export default function ArticlesIndexPage({ snapshot, preview }: Props) {
    const { locale = 'en-US', asPath } = useRouter();
    const url = useUrl({ locale });
    const baseUrl = `${url?.protocol}//${url?.host}`;
    const path = asPath.split('/').filter(Boolean);
    const noIndex = getNoIndexValueFromMakeSwift({ snapshot, url });
    return (<>
			<NextSeo canonical={`${baseUrl}${getLocalizedFromEnglish(locale as Locale, `/articles/`)}`} languageAlternates={[
            { hrefLang: 'en-us', href: `${getBaseUrl('en-US')}/articles/` },
            { hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}/articles/` },
            { hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}/articles/` },
            { hrefLang: 'fr-fr', href: `${getBaseUrl('fr-FR')}/articles/` },
            { hrefLang: 'it-it', href: `${getBaseUrl('it-IT')}/articoli/` },
            { hrefLang: 'nl-nl', href: `${getBaseUrl('nl-NL')}/artikelen/` },
            { hrefLang: 'es-es', href: `${getBaseUrl('es-ES')}/articulos/` },
            { hrefLang: 'es-mx', href: `${getBaseUrl('es-MX')}/articulos/` },
            { hrefLang: 'de-de', href: `${getBaseUrl('de-DE')}/artikel/` },
            { hrefLang: 'x-default', href: `${getBaseUrl('en-US')}/articles/` }
        ]} openGraph={{
            url: `${baseUrl}${getLocalizedFromEnglish(locale as Locale, `/articles/`)}`,
            locale: getSeoLocale(locale),
            type: 'website',
            siteName: 'BigCommerce'
        }} noindex={noIndex}/>
			<BreadcrumbJsonLd itemListElements={[
            {
                position: 1,
                name: 'Homepage',
                item: `${getBaseUrl(locale)}/`
            },
            {
                position: 2,
                name: 'Articles',
                item: `${getBaseUrl(locale)}/${path.at(0)}/`
            }
        ]}/>
			<PreviewToast preview={preview}/>
			<MakeswiftPage snapshot={snapshot} runtime={runtime}/>
		</>);
}
async function getStaticProps({ previewData, preview, locale }: GetStaticPropsContext<{
    slug: string;
}, {
    makeswift: boolean;
}>) {
    const snapshot = await getPageSnapshot({
        pathname: '/articles',
        isMakeswiftPreview: previewData?.makeswift == true,
        previewData,
        locale,
        timeout: {
            ms: 10000,
            message: `Timeout request getPageSnapshot for "/articles" – Locale: ${locale}`
        }
    });
    if (snapshot == null) {
        return { notFound: true, revalidate: 3600 };
    }
    return {
        props: {
            snapshot,
            fallback: {
                [serializeCacheKey('articleFeed', { type: 'latest', locale })]: await contentful({
                    preview: Boolean(preview)
                }).request(ArticleFeedCarouselDocument, {
                    ...getArticleParams({ type: 'latest', preview }),
                    locale
                }),
                [serializeCacheKey('articleFeed', { type: 'root', locale })]: await contentful({
                    preview: Boolean(preview)
                }).request(ArticleFeedCarouselDocument, {
                    ...getArticleParams({ type: 'root', preview }),
                    locale
                }),
                [serializeCacheKey('articleFeed', { type: 'popular', locale })]: await contentful({
                    preview: Boolean(preview)
                }).request(ArticleFeedCarouselDocument, {
                    ...getArticleParams({ type: 'popular', preview }),
                    locale
                })
            },
            previewData: previewData?.makeswift == true,
            preview: Boolean(preview),
            pageId: snapshot.document.id
        },
        revalidate: 3600
    };
}

    async function __Next_Translate__getStaticProps__1934a36eca5__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/articles/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1934a36eca5__ as getStaticProps }
  